import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useGeocodedAddress from './useGeocodedAddress.js';
import { usePoiLabelPrefs } from './usePoiLabelPrefs.js';
import { findBestPOI, formatPoiLabel } from '../lib/poiMatcher.js';

/**
 * Returns POI label when enabled + match exists, else geocoded address.
 * `surface` must be one of 'liveMap', 'replay', 'reports', 'notifications'.
 * POIs are fetched once at app bootstrap via usePOIs() in App.jsx.
 */
export default function useAddressLabel(lat, lng, fallback, surface) {
  const prefs = usePoiLabelPrefs();
  const poisDict = useSelector((s) => s.pois?.items || {});
  const orgId = useSelector((s) => s.org?.id);

  // Convert dict to array for findBestPOI
  const poisArray = useMemo(() => Object.values(poisDict), [poisDict]);

  const poiLabel = useMemo(() => {
    if (!prefs.enabled || !prefs.surfaces[surface]) return null;
    if (!poisArray?.length) return null;
    const match = findBestPOI(lat, lng, poisArray, { sources: prefs.sources, orgId });
    return formatPoiLabel(match);
  }, [lat, lng, poisArray, prefs, orgId, surface]);

  const geocoded = useGeocodedAddress(lat, lng, fallback);
  return poiLabel ?? geocoded;
}
